/**
 * Centralized Hash Management System
 *
 * This class provides a unified interface for all hash-related operations
 * across the application, ensuring consistent behavior and easier maintenance.
 */

import type {
  HashChangeOptions,
  HashNavigationOptions,
  HashManagerConfig,
  HashChangeHandler,
  HashEventDetails,
} from './types';
import { log } from '@/utils/logger';

export class HashManager {
  private static instance: HashManager;
  private config: HashManagerConfig;
  private handlers: Set<HashChangeHandler> = new Set();
  private currentHash: string = '';
  private previousHash: string = '';

  constructor(config?: Partial<HashManagerConfig>) {
    this.config = {
      defaultScrollOffset: 80,
      defaultScrollBehavior: 'smooth',
      defaultUpdateHistory: true,
      ...config,
    };

    log.info({
      event: 'hash_manager_initialized',
      message: 'HashManager instance created',
      labels: { module: 'HashManager' },
      stringify: { config: this.config },
    });

    if (typeof window !== 'undefined') {
      this.currentHash = this.getCurrentHash();
      this.setupHashChangeListener();

      log.debug({
        event: 'hash_manager_ready',
        message: 'HashManager ready for browser use',
        labels: { module: 'HashManager' },
        stringify: { initialHash: this.currentHash },
      });
    }
  }

  /**
   * Get singleton instance of HashManager
   */
  static getInstance(config?: Partial<HashManagerConfig>): HashManager {
    if (!HashManager.instance) {
      HashManager.instance = new HashManager(config);
    }
    return HashManager.instance;
  }

  /**
   * Get current hash without the # symbol
   */
  getCurrentHash(): string {
    if (typeof window === 'undefined') return '';
    return decodeURIComponent(window.location.hash).replace('#', '');
  }

  /**
   * Set hash in URL with options
   */
  setHash(hash: string, options: HashChangeOptions = {}): void {
    const {
      scroll = true,
      smooth = this.config.defaultScrollBehavior === 'smooth',
      offset = this.config.defaultScrollOffset,
      updateHistory = this.config.defaultUpdateHistory,
    } = options;

    // Clean hash (remove # if present)
    const cleanHash = hash.replace('#', '');

    log.debug({
      event: 'hash_change_requested',
      message: 'Setting new hash',
      labels: { module: 'HashManager' },
      stringify: { hash: cleanHash, options },
    });

    if (typeof window === 'undefined') return;

    this.previousHash = this.currentHash;
    this.currentHash = cleanHash;

    if (updateHistory) {
      const newUrl = cleanHash
        ? `${window.location.pathname}${window.location.search}#${cleanHash}`
        : `${window.location.pathname}${window.location.search}`;

      window.history.replaceState({}, '', newUrl);

      log.debug({
        event: 'hash_url_updated',
        message: 'URL updated with new hash',
        labels: { module: 'HashManager' },
        stringify: { newUrl, cleanHash },
      });
    }

    if (scroll && cleanHash) {
      this.scrollToElement(cleanHash, { smooth, offset });
    }

    log.info({
      event: 'hash_set_complete',
      message: 'Hash change completed',
      labels: { module: 'HashManager' },
      stringify: {
        hash: cleanHash,
        previousHash: this.previousHash,
        scrolled: scroll && !!cleanHash,
        historyUpdated: updateHistory,
      },
    });

    this.notifyHandlers(cleanHash);
  }

  /**
   * Navigate to a new path with hash using Next.js router
   */
  navigateWithHash(pathname: string, hash: string, options: HashNavigationOptions = {}): void {
    const { replace = false, locale } = options;

    const cleanHash = hash.replace('#', '');
    const fullPath = cleanHash ? `${pathname}#${cleanHash}` : pathname;

    log.info({
      event: 'hash_navigation_requested',
      message: 'Navigating with hash',
      labels: { module: 'HashManager' },
      stringify: { pathname, hash: cleanHash, replace, locale },
    });

    // Note: This requires the router to be passed in or accessed via context
    // For now, we'll emit an event that components can listen to
    this.emitNavigationEvent(fullPath, replace, locale);

    // Update internal state
    this.previousHash = this.currentHash;
    this.currentHash = cleanHash;
  }

  /**
   * Scroll to element by hash ID
   */
  scrollToElement(hash: string, options: { smooth?: boolean; offset?: number } = {}): boolean {
    const { smooth = this.config.defaultScrollBehavior === 'smooth', offset = this.config.defaultScrollOffset } =
      options;

    if (typeof window === 'undefined') return false;

    const element = document.getElementById(hash);
    if (!element) {
      log.warning({
        event: 'hash_element_not_found',
        message: `Element with ID "${hash}" not found`,
        labels: { module: 'HashManager' },
        stringify: { hash },
      });
      return false;
    }

    const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
    const scrollTop = Math.max(0, elementTop - offset);

    window.scrollTo({
      top: scrollTop,
      behavior: smooth ? 'smooth' : 'auto',
    });

    log.debug({
      event: 'hash_scroll_performed',
      message: 'Scrolled to hash element',
      labels: { module: 'HashManager' },
      stringify: { hash, scrollTop, offset, smooth },
    });

    return true;
  }

  /**
   * Clear current hash
   */
  clearHash(options: Pick<HashChangeOptions, 'updateHistory'> = {}): void {
    log.debug({
      event: 'hash_clear_requested',
      message: 'Clearing current hash',
      labels: { module: 'HashManager' },
      stringify: { previousHash: this.currentHash, options },
    });

    this.setHash('', options);
  }

  /**
   * Add handler for hash changes
   */
  onHashChange(handler: HashChangeHandler): () => void {
    this.handlers.add(handler);

    // log.debug({
    //   event: 'hash_handler_added',
    //   message: 'Hash change handler registered',
    //   labels: { module: 'HashManager' },
    //   stringify: { totalHandlers: this.handlers.size },
    // });

    // Return unsubscribe function
    return () => {
      this.handlers.delete(handler);

      // log.debug({
      //   event: 'hash_handler_removed',
      //   message: 'Hash change handler unregistered',
      //   labels: { module: 'HashManager' },
      //   stringify: { totalHandlers: this.handlers.size },
      // });
    };
  }

  /**
   * Check if current hash matches given hash
   */
  isCurrentHash(hash: string): boolean {
    return this.currentHash === hash.replace('#', '');
  }

  /**
   * Get element for current hash
   */
  getCurrentElement(): HTMLElement | null {
    if (!this.currentHash || typeof window === 'undefined') return null;
    return document.getElementById(this.currentHash);
  }

  /**
   * Private: Setup browser hash change listener
   */
  private setupHashChangeListener(): void {
    const handleHashChange = (event: Event) => {
      const newHash = this.getCurrentHash();
      if (newHash !== this.currentHash) {
        log.info({
          event: 'hash_changed_browser',
          message: 'Hash changed via browser event',
          labels: { module: 'HashManager' },
          stringify: {
            previousHash: this.currentHash,
            newHash,
            eventType: event.type,
            source: 'browser',
          },
        });

        this.previousHash = this.currentHash;
        this.currentHash = newHash;
        this.notifyHandlers(newHash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    // Also listen for popstate for better back/forward support
    window.addEventListener('popstate', handleHashChange);

    // log.debug({
    //   event: 'hash_listeners_setup',
    //   message: 'Browser hash change listeners registered',
    //   labels: { module: 'HashManager' },
    // });
  }

  /**
   * Private: Notify all registered handlers
   */
  private notifyHandlers(hash: string): void {
    const element = hash ? document.getElementById(hash) : null;

    const eventDetails: HashEventDetails = {
      hash,
      previousHash: this.previousHash,
      element,
      timestamp: Date.now(),
    };

    log.info({
      event: 'hash_handlers_notified',
      message: 'Notifying hash change handlers',
      labels: { module: 'HashManager' },
      stringify: {
        hash,
        previousHash: this.previousHash,
        hasElement: !!element,
        handlerCount: this.handlers.size,
      },
    });

    this.handlers.forEach(handler => {
      try {
        handler(hash, element);
      } catch (error) {
        log.error({
          event: 'hash_handler_error',
          message: 'Error in hash change handler',
          labels: { module: 'HashManager' },
          stringify: {
            hash,
            error: error instanceof Error ? error.message : String(error),
          },
        });
      }
    });

    // Emit custom event for components that prefer event listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('hashManagerChange', {
          detail: eventDetails,
        })
      );

      log.debug({
        event: 'hash_custom_event_dispatched',
        message: 'Dispatched hashManagerChange custom event',
        labels: { module: 'HashManager' },
        stringify: { hash },
      });
    }
  }

  /**
   * Private: Emit navigation event for Next.js router integration
   */
  private emitNavigationEvent(path: string, replace: boolean, locale?: string): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('hashManagerNavigate', {
          detail: { path, replace, locale },
        })
      );

      log.debug({
        event: 'hash_navigation_event_dispatched',
        message: 'Dispatched hashManagerNavigate custom event',
        labels: { module: 'HashManager' },
        stringify: { path, replace, locale },
      });
    }
  }
}

// Export singleton instance
export const hashManager = HashManager.getInstance();
